import React from 'react'
import { Field, Form, Formik, FormikState, useFormikContext } from 'formik'
import { Button, Element } from 'react-bulma-components'
import Modal, { ModalActions } from '../../../components/modal'
import TextareaField from '../../../components/form/fields/textarea'
import RadioField from '../../../components/form/fields/radio'
import {
  RequestCancellationFormValues,
  ResolveCancellationFormValues,
} from './pre-billing-cancellation-modals.types'
import {
  validateRequestCancellationForm,
  validateResolveCancellationForm,
} from './pre-billing-cancellation-modals.validation'

const REQUEST_CANCELLATION_FORM_ID = 'pre-billing-request-cancellation-form'
const RESOLVE_CANCELLATION_FORM_ID = 'pre-billing-resolve-cancellation-form'

const initialRequestValues: RequestCancellationFormValues = {
  requestComment: '',
}

const initialResolveValues: ResolveCancellationFormValues = {
  decision: 'accepted',
  resolutionComment: '',
}

/**
 * Resets Formik state when the modal opens (transition to displayed) so each open starts clean.
 */
function useResetFormWhenModalOpens<T>(
  isDisplayed: boolean,
  resetForm: (nextState?: Partial<FormikState<T>>) => void,
  nextValues: T,
): void {
  const prevDisplayed = React.useRef(false)

  React.useEffect(() => {
    if (isDisplayed && !prevDisplayed.current) {
      resetForm({ values: nextValues })
    }
    prevDisplayed.current = isDisplayed
  }, [isDisplayed, resetForm, nextValues])
}

// --- Request cancellation ---

const RequestCancellationModalInner: React.FC<{ actions: ModalActions }> = ({ actions }) => {
  const { isSubmitting, resetForm } = useFormikContext<RequestCancellationFormValues>()
  useResetFormWhenModalOpens(actions.isDisplayed, resetForm, initialRequestValues)

  return (
    <Modal
      actions={actions}
      title="Demander l'annulation de la pré-facturation"
      dataTest="pre-billing-request-cancellation-modal"
      footer={
        <>
          <Button
            type="button"
            onClick={() => actions.setIsDisplayed(false)}
            disabled={isSubmitting}
            data-test="pre-billing-request-cancellation-cancel"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            form={REQUEST_CANCELLATION_FORM_ID}
            color="danger"
            loading={isSubmitting}
            disabled={isSubmitting}
            ml={2}
            data-test="pre-billing-request-cancellation-submit"
          >
            Envoyer la demande
          </Button>
        </>
      }
    >
      <Element className="mb-4">
        Expliquez pourquoi vous souhaitez annuler cette pré-facturation. L&apos;autre partie devra
        accepter ou refuser avec un commentaire obligatoire.
      </Element>
      <Form id={REQUEST_CANCELLATION_FORM_ID}>
        <Field
          name="requestComment"
          component={TextareaField}
          label="Commentaire"
          required
          minRows={5}
          disabled={isSubmitting}
          data-test="pre-billing-request-cancellation-comment"
        />
      </Form>
    </Modal>
  )
}

export interface RequestCancellationModalProps {
  actions: ModalActions
  onSubmit: (values: RequestCancellationFormValues) => Promise<void>
}

/**
 * Modal to submit a cancellation request with a mandatory comment (Formik + fields).
 */
export const RequestCancellationModal: React.FC<RequestCancellationModalProps> = ({
  actions,
  onSubmit,
}) => (
  <Formik<RequestCancellationFormValues>
    initialValues={initialRequestValues}
    enableReinitialize
    validate={validateRequestCancellationForm}
    onSubmit={async (values, { resetForm }) => {
      await onSubmit(values)
      resetForm({ values: initialRequestValues })
      actions.setIsDisplayed(false)
    }}
  >
    <RequestCancellationModalInner actions={actions} />
  </Formik>
)

// --- Resolve cancellation ---

const ResolveCancellationModalInner: React.FC<{ actions: ModalActions }> = ({ actions }) => {
  const { isSubmitting, resetForm } = useFormikContext<ResolveCancellationFormValues>()
  useResetFormWhenModalOpens(actions.isDisplayed, resetForm, initialResolveValues)

  return (
    <Modal
      actions={actions}
      title="Répondre à la demande d'annulation"
      dataTest="pre-billing-resolve-cancellation-modal"
      footer={
        <>
          <Button
            type="button"
            onClick={() => actions.setIsDisplayed(false)}
            disabled={isSubmitting}
            data-test="pre-billing-resolve-cancellation-close"
          >
            Fermer
          </Button>
          <Button
            type="submit"
            form={RESOLVE_CANCELLATION_FORM_ID}
            color="primary"
            loading={isSubmitting}
            disabled={isSubmitting}
            ml={2}
            data-test="pre-billing-resolve-cancellation-submit"
          >
            Confirmer
          </Button>
        </>
      }
    >
      <Form id={RESOLVE_CANCELLATION_FORM_ID}>
        <Element mb={4}>
          <Field
            name="decision"
            component={RadioField}
            label="Décision"
            options={[
              { value: 'accepted', label: "Accepter l'annulation" },
              { value: 'refused', label: 'Refuser' },
            ]}
            disabled={isSubmitting}
            data-test="pre-billing-resolve-cancellation-decision"
          />
        </Element>
        <Field
          name="resolutionComment"
          component={TextareaField}
          label="Commentaire"
          required
          minRows={5}
          disabled={isSubmitting}
          data-test="pre-billing-resolve-cancellation-comment"
        />
      </Form>
    </Modal>
  )
}

export interface ResolveCancellationModalProps {
  actions: ModalActions
  onSubmit: (values: ResolveCancellationFormValues) => Promise<void>
}

/**
 * Modal for the counterparty to accept or refuse a pending cancellation request (Formik + fields).
 */
export const ResolveCancellationModal: React.FC<ResolveCancellationModalProps> = ({
  actions,
  onSubmit,
}) => (
  <Formik<ResolveCancellationFormValues>
    initialValues={initialResolveValues}
    enableReinitialize
    validate={validateResolveCancellationForm}
    onSubmit={async (values, { resetForm }) => {
      await onSubmit(values)
      resetForm({ values: initialResolveValues })
      actions.setIsDisplayed(false)
    }}
  >
    <ResolveCancellationModalInner actions={actions} />
  </Formik>
)
